import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useCompanion } from './companion'

/** The assistant's view of the band — the id the shell draws it under. */
export const ASSISTANT_VIEW = 'assistant'

/**
 * Which conversation the assistant is having.
 *
 * Only that. WHERE it appears is the companion band's business — the assistant
 * is one view of the one band the shell opens, not a panel of its own, so
 * opening it here opens that band at its view and closing the band ends the
 * conversation. A page says "open the assistant for my skill" and nothing more.
 */

/**
 * The skills that exist, mirroring `SKILLS` on the API. A page opening the
 * assistant names one, and the server loads that skill's tools and prose.
 *
 * Members is here now that the per-org opt-in exists. With it off the members
 * assistant still runs — on counts, totals and what a change would touch — and
 * the tools that would hand a model the roster refuse until an org allows it.
 */
export type AssistantSkill =
  | 'scheduled-messages'
  | 'rules'
  | 'campaigns'
  | 'members'
  | 'pages'

export type AssistantApplyResult = {
  tool: string
  ok: boolean
  error?: string
  id?: string
  key?: string
}

export type AssistantConfig = {
  skill: AssistantSkill
  spaceId: string
  title: string
  /** Query keys to refresh once a change lands, so the table beside it updates. */
  invalidateOnApply?: unknown[]
  /** Seeds the first message — how "Add new with AI" differs from the button. */
  opening?: string
  /** Browser-local family names the page font picker has already read. */
  localFontFamilies?: string[]
  /** Continue the page workflow after a confirmed proposal lands. */
  onApplied?: (results: AssistantApplyResult[]) => void
}

type AssistantApi = {
  config: AssistantConfig | null
  open: (config: AssistantConfig) => void
  close: () => void
}

const AssistantContext = createContext<AssistantApi | null>(null)

export function AssistantProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AssistantConfig | null>(null)
  const companion = useCompanion()

  const { open: openBand, close: closeBand } = companion
  const open = useCallback(
    (c: AssistantConfig) => {
      setConfig(c)
      openBand(ASSISTANT_VIEW)
    },
    [openBand],
  )
  const close = useCallback(() => {
    setConfig(null)
    closeBand()
  }, [closeBand])

  const api = useMemo<AssistantApi>(
    () => ({ config, open, close }),
    [config, open, close],
  )
  return (
    <AssistantContext.Provider value={api}>
      {children}
    </AssistantContext.Provider>
  )
}

export function useAssistant(): AssistantApi {
  const ctx = useContext(AssistantContext)
  if (!ctx) {
    throw new Error('useAssistant must be used inside the app shell.')
  }
  return ctx
}

/** A page's own opener, so the call site reads as one line. */
export function useOpenAssistant() {
  const { open } = useAssistant()
  return useCallback((config: AssistantConfig) => open(config), [open])
}
